import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, LOGO_URL, inr, SOCIALS, getJoinRef } from "@/lib/api";
import { PageBackground } from "@/components/PageBackground";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GuideBookButton } from "@/components/GuideBookButton";
import { PriceTag } from "@/components/PriceTag";
import { Play, Images, ArrowRight, Facebook, Instagram, MessageCircle, Send, Loader2, Award, MapPin, FileCheck2, CalendarDays, Users, Gift, Share2, TrendingUp, Briefcase, Building2, LogIn, UserPlus, Crown, Plane, FileDown } from "lucide-react";
import { jobPhotoSrc } from "@/lib/jobPhotos";
import { PackageSlider } from "@/components/PackagePhotos";
import { LiveDepartures } from "@/components/LiveDepartures";
import { CATEGORIES } from "@/components/shop/categories";
import { IslamicStar } from "@/components/shop/CategoryTiles";
import { PinnedCreatives } from "@/components/ai/PinnedCreatives";

const ACCESS = [
  { to: "/login", label: "Company Login", sub: "Management console · Office staff", icon: Crown, tid: "side-admin" },
  { to: "/agent", label: "Associate Partner & Member Login", sub: "Join, earn & track commission", icon: UserPlus, tid: "side-partner" },
  { to: "/customer-login", label: "Umrah Pilgrim & Job Customer Login", sub: "One mobile number login", icon: Plane, tid: "side-pilgrim" },
  { href: `${API}/prospectus/member.pdf`, label: "Download Prospectus", sub: "Company profile, packages & jobs (PDF)", icon: FileDown, tid: "side-prospectus" },
  { href: `${API}/prospectus/guide.pdf`, label: "Umrah Guide Book", sub: "Step-by-step rites, all duas & ziyarat (PDF)", icon: FileDown, tid: "side-guide" },
];

function AccessPanel() {
  const ref = getJoinRef();
  const prospectusHref = `${API}/prospectus/member.pdf?origin=${encodeURIComponent(window.location.origin)}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`;
  const guideHref = `${API}/prospectus/guide.pdf?origin=${encodeURIComponent(window.location.origin)}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`;
  return (
    <aside className="lg:fixed lg:left-0 lg:top-0 lg:bottom-0 lg:w-64 lg:border-r border-border/60 bg-[#031C15]/95 lg:overflow-y-auto z-40" data-testid="access-panel">
      <div className="hidden lg:flex flex-col items-center px-4 pt-6 pb-4 border-b border-border/50">
        <img src={LOGO_URL} alt="Bengal Umrah House" className="w-full max-w-[180px] rounded-xl" />
      </div>
      <p className="hidden lg:block eyebrow text-gold px-4 pt-5 pb-2">Join · Login · Sign up</p>
      <div className="flex lg:flex-col gap-2 px-3 lg:px-3 py-3 lg:py-0 overflow-x-auto">
        {ACCESS.map((a) => a.tid === "side-guide" ? (
          <div key={a.tid} className="flex-shrink-0 lg:w-full"><GuideBookButton testid="side-guide" href={guideHref} small={false} /></div>
        ) : a.href ? (
          <a key={a.tid} href={a.tid === "side-prospectus" ? prospectusHref : a.tid === "side-guide" ? guideHref : a.href} target="_blank" rel="noopener noreferrer" data-testid={a.tid}
            className="flex-shrink-0 flex items-center gap-3 rounded-xl border border-gold/25 bg-card/40 px-3 py-2.5 hover:border-gold/60 hover:bg-gold/10 transition-colors">
            <span className="w-8 h-8 rounded-lg bg-gold/15 text-gold flex items-center justify-center flex-shrink-0"><a.icon className="w-4 h-4" /></span>
            <span className="min-w-0"><span className="block text-xs font-semibold text-white whitespace-nowrap lg:whitespace-normal">{a.label}</span><span className="hidden lg:block text-[10px] text-muted-foreground">{a.sub}</span></span>
          </a>
        ) : (
          <Link key={a.to} to={a.to} data-testid={a.tid}
            className="flex-shrink-0 flex items-center gap-3 rounded-xl border border-gold/25 bg-card/40 px-3 py-2.5 hover:border-gold/60 hover:bg-gold/10 transition-colors">
            <span className="w-8 h-8 rounded-lg bg-gold/15 text-gold flex items-center justify-center flex-shrink-0"><a.icon className="w-4 h-4" /></span>
            <span className="min-w-0"><span className="block text-xs font-semibold text-white whitespace-nowrap lg:whitespace-normal">{a.label}</span><span className="hidden lg:block text-[10px] text-muted-foreground">{a.sub}</span></span>
          </Link>
        ))}
      </div>
    </aside>
  );
}

const WHATSAPP = "https://wa.me/919820466270?text=" + encodeURIComponent("Assalamu Alaikum! I want to know about your Umrah packages.");

const FEATURES = [
  { icon: MapPin, title: "Umrah & Hajj", desc: "Curated packages with fixed departure dates and family booking for your group." },
  { icon: FileCheck2, title: "Document Vault", desc: "Upload passport & PAN, download visa, tickets and vouchers from your portal." },
  { icon: CalendarDays, title: "Fixed Group Dates", desc: "Reserve seats on confirmed departures with experienced Muallems leading each group." },
  { icon: Users, title: "Partner Network", desc: "Grow a team of associates and co-workers and earn on every pilgrim you bring." },
];

const PARTNER_BENEFITS = [
  "Earn commission on every Umrah / Hajj pilgrim you bring",
  "Build your own team of co-workers and earn across tiers",
  "Your own premium visiting card, flyer and referral links",
  "Track your pilgrims, bookings and earnings in one console",
];

export default function Showcase() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [pkgPhotos, setPkgPhotos] = useState({});
  const [muallems, setMuallems] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Please enter your name and phone number"); return; }
    setSending(true);
    try {
      const r = await fetch(`${API}/public/enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success(d.message || "Enquiry sent!");
      setForm({ name: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Could not send enquiry. Please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    fetch(`${API}/public/media`).then((r) => r.json()).then((d) => setItems(Array.isArray(d) ? d : [])).catch(() => setItems([])).finally(() => setLoading(false));
    fetch(`${API}/public/packages`).then((r) => r.json()).then((d) => setPackages(d.packages || [])).catch(() => setPackages([]));
    fetch(`${API}/public/jobs`).then((r) => r.json()).then((d) => setJobs(Array.isArray(d) ? d : [])).catch(() => setJobs([]));
    fetch(`${API}/public/package-photos`).then((r) => r.json()).then((d) => setPkgPhotos(d || {})).catch(() => {});
    fetch(`${API}/public/muallems`).then((r) => r.json()).then((d) => setMuallems(Array.isArray(d) ? d : [])).catch(() => setMuallems([]));
    fetch(`${API}/public/testimonials`).then((r) => r.json()).then((d) => setTestimonials(Array.isArray(d) ? d : [])).catch(() => setTestimonials([]));
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="relative min-h-screen" data-testid="showcase-page">
      <PageBackground />
      <AccessPanel />
      <div className="lg:pl-64">
      {/* Header */}
      <header className="h-16 flex items-center justify-between px-4 sm:px-6 border-b border-border/60 glass sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Bengal Umrah House" className="w-10 h-10 rounded-full ring-1 ring-gold/50" />
          <div>
            <p className="font-serif text-sm font-semibold">Bengal Umrah House</p>
            <p className="eyebrow text-gold/80">Umrah · Hajj · Ziyarat</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => scrollTo("packages")} data-testid="nav-packages" className="hidden sm:inline text-xs text-emerald-100/70 hover:text-gold transition-colors px-2">Packages</button>
          <button onClick={() => scrollTo("jobs")} data-testid="nav-jobs" className="hidden sm:inline text-xs text-emerald-100/70 hover:text-gold transition-colors px-2">Jobs</button>
          <button onClick={() => scrollTo("partner")} data-testid="nav-partner" className="hidden sm:inline text-xs text-emerald-100/70 hover:text-gold transition-colors px-2">Partner</button>
          <button onClick={() => scrollTo("gallery")} data-testid="nav-gallery" className="hidden sm:inline text-xs text-emerald-100/70 hover:text-gold transition-colors px-2">Gallery</button>
          <Link to="/customer-login">
            <Button size="sm" data-testid="showcase-portal-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-1.5 text-xs">
              Customer Login <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </header>
      <PinnedCreatives className="px-4 sm:px-6 py-4" />

      {/* Hero banner */}
      <section className="relative overflow-hidden bg-[#0a0d1c]" data-testid="showcase-hero">
        <img src="/hero-celestial.jpg" alt="Bengal Umrah House — Sacred Journeys, Celestial Guidance" data-testid="showcase-hero-bg"
          className="absolute inset-0 w-full h-full object-cover lg:left-auto lg:right-0 lg:w-auto lg:max-w-none" />
        <div className="absolute inset-0 bg-[#0a0d1c]/70 lg:hidden" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,13,28,0.97) 22%, rgba(10,13,28,0.72) 48%, rgba(10,13,28,0.15) 75%, rgba(10,13,28,0) 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <p className="eyebrow text-gold mb-4 tracking-[0.3em]">Labbaik Allahumma Labbaik</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-white max-w-2xl leading-tight">Your sacred journey, guided with care.</h1>
          <p className="text-emerald-100/75 text-sm sm:text-base mt-5 max-w-xl leading-relaxed">
            Umrah & Hajj packages with fixed departure dates, experienced Muallems, and a partner network you can grow — all from Bengal Umrah House.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button onClick={() => scrollTo("packages")} data-testid="hero-packages-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
              Explore Packages <ArrowRight className="w-4 h-4" />
            </Button>
            <Link to="/agent">
              <Button variant="outline" data-testid="hero-partner-btn" className="border-gold/50 text-gold hover:bg-gold/10 font-semibold bg-transparent">
                Join as Partner
              </Button>
            </Link>
          </div>
          <div className="flex items-center gap-3 mt-8">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="showcase-whatsapp-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 text-[#7ee8a8] hover:bg-[#25D366]/25 transition-colors text-xs font-semibold">
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <a href={SOCIALS.facebook} target="_blank" rel="noopener noreferrer" data-testid="showcase-facebook-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/15 border border-[#1877F2]/40 text-[#8ab8f8] hover:bg-[#1877F2]/25 transition-colors text-xs font-semibold">
              <Facebook className="w-4 h-4" /> Facebook
            </a>
            <a href={SOCIALS.instagram} target="_blank" rel="noopener noreferrer" data-testid="showcase-instagram-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E1306C]/15 border border-[#E1306C]/40 text-[#f48fb1] hover:bg-[#E1306C]/25 transition-colors text-xs font-semibold">
              <Instagram className="w-4 h-4" /> Instagram
            </a>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 -mt-10 relative z-10" data-testid="showcase-features">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <Card key={f.title} data-testid={`feature-${f.title}`} className="p-5 bg-card/70 backdrop-blur border-border/60 card-hover">
              <div className="w-10 h-10 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center mb-3">
                <f.icon className="w-5 h-5 text-gold" />
              </div>
              <p className="font-serif font-semibold text-white">{f.title}</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Overview */}
      <section className="px-4 sm:px-8 pt-14 max-w-6xl mx-auto" data-testid="showcase-overview">
        <p className="eyebrow text-gold mb-3">Overview</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <button type="button" onClick={() => scrollTo("packages")} data-testid="overview-packages-btn" className="group text-left rounded-2xl overflow-hidden border border-gold/30 relative h-36 hover:border-gold transition-colors">
            <img src="/holy/kaaba.jpg" alt="Umrah packages" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <span className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/20" />
            <span className="relative p-5 flex flex-col h-full justify-end"><span className="font-serif text-2xl text-white font-semibold">Umrah Packages</span><span className="text-xs text-gold">{packages.length} packages · view prices & dates →</span></span>
          </button>
          <button type="button" onClick={() => scrollTo("jobs")} data-testid="overview-jobs-btn" className="group text-left rounded-2xl overflow-hidden border border-gold/30 relative h-36 hover:border-gold transition-colors">
            <img src="/jobs/construction.jpg" alt="Jobs abroad" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <span className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/20" />
            <span className="relative p-5 flex flex-col h-full justify-end"><span className="font-serif text-2xl text-white font-semibold">Jobs & Employment</span><span className="text-xs text-gold">{jobs.length} openings · apply online →</span></span>
          </button>
        </div>
      </section>

      {/* Packages */}
      {packages.length > 0 && (
        <section id="packages" className="px-4 sm:px-8 py-16 max-w-6xl mx-auto scroll-mt-20" data-testid="showcase-packages">
          <p className="eyebrow text-gold text-center mb-2">Packages</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">Umrah & Hajj departures</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {packages.map((p) => (
              <Card key={p.id} data-testid={`showcase-package-${p.id}`} className="overflow-hidden bg-card/40 border-border/60 card-hover flex flex-col">
                <PackageSlider items={pkgPhotos[p.id]} />
                <div className="p-5 flex flex-col flex-1">
                  <p className="font-serif font-semibold text-white text-lg">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-1.5 flex-1 leading-relaxed">{p.desc}</p>
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider">per person</p>
                    <PriceTag big price={p.price} mrp={p.mrp} until={p.offer_until} testid={`showcase-price-${p.id}`} />
                  </div>
                  <Link to="/portal" className="mt-4 block">
                    <Button size="sm" data-testid={`showcase-book-${p.id}`} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-xs gap-1.5">Book Now <ArrowRight className="w-3.5 h-3.5" /></Button>
                  </Link>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <LiveDepartures />

      <section id="shop" className="px-4 sm:px-8 py-14 max-w-6xl mx-auto" data-testid="showcase-shop-categories">
        <p className="eyebrow text-gold text-center mb-2">VIP Collection</p>
        <p className="font-arabic text-2xl text-gold text-center" dir="rtl">متجر البركة</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-8">Shop sacred essentials</h2>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          {CATEGORIES.map((c) => (
            <Link key={c.key} to={`/shop?cat=${c.key}`} data-testid={`showcase-cat-${c.key}`} className="group flex flex-col items-center rounded-2xl border border-gold/25 bg-black/30 p-3 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-gold/70">
              <span className="relative w-14 h-14 flex items-center justify-center"><IslamicStar className="absolute inset-0 w-full h-full transition-transform duration-700 group-hover:rotate-45" /><span className="relative font-arabic text-[15px] text-[#FFE9A8]" dir="rtl">{c.ar}</span></span>
              <span className="mt-2 text-[11px] font-semibold text-white/90 text-center leading-tight">{c.label}</span>
            </Link>
          ))}
        </div>
        <div className="text-center mt-7"><Link to="/shop" data-testid="showcase-open-shop" className="inline-flex items-center h-11 px-7 rounded-full font-bold text-[#05261D] bg-[linear-gradient(135deg,#D4AF37_0%,#F3E5AB_50%,#C5A059_100%)] hover:shadow-[0_0_24px_rgba(212,175,55,0.55)] transition-shadow">Enter the VIP Shop</Link></div>
      </section>

      {/* Jobs */}
      <section id="jobs" className="px-4 sm:px-8 py-16 max-w-6xl mx-auto scroll-mt-20" data-testid="showcase-jobs">
        <p className="eyebrow text-gold text-center mb-2">Employment</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">Jobs abroad</h2>
        {jobs.length === 0 && <p className="text-sm text-muted-foreground text-center">New openings coming soon.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {jobs.slice(0, 6).map((j) => (
            <Card key={j.id} data-testid={`showcase-job-${j.id}`} className="overflow-hidden bg-card/40 border-border/60 card-hover flex flex-col">
              <img src={jobPhotoSrc(j.photo)} alt={j.post} className="h-40 w-full object-cover" />
              <div className="p-5 flex flex-col flex-1">
                <p className="font-serif font-semibold text-white text-lg">{j.post}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Building2 className="w-3 h-3 text-gold" /> {j.company}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> {j.country}</p>
                <div className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between">
                  <div><p className="text-[10px] text-muted-foreground uppercase tracking-wider">service charge</p><p className="text-gold font-mono text-lg">{inr(j.service_charge)}</p></div>
                  <Link to={`/jobs?job=${j.id}`}><Button size="sm" data-testid={`showcase-apply-${j.id}`} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold text-xs gap-1">Apply <ArrowRight className="w-3.5 h-3.5" /></Button></Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
        {jobs.length > 0 && <div className="text-center mt-8"><Link to="/jobs"><Button variant="outline" data-testid="showcase-all-jobs-btn" className="border-gold/50 text-gold hover:bg-gold/10 bg-transparent gap-2"><LogIn className="w-4 h-4" /> View all jobs & candidate login</Button></Link></div>}
      </section>

      {/* Partner / Franchise program */}
      <section id="partner" className="brand-gradient border-y border-gold/20 scroll-mt-16" data-testid="showcase-partner">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow text-gold mb-2">Partner Program</p>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white leading-tight">Grow a team, earn on every journey.</h2>
            <p className="text-emerald-100/75 text-sm mt-4 leading-relaxed">
              Become an Associate Partner of Bengal Umrah House. Get a unique referral code — every pilgrim and co-worker who joins with your code is placed in your team, and you earn on their bookings.
            </p>
            <Link to="/agent" className="inline-block mt-6">
              <Button data-testid="partner-join-btn" className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
                Become a Partner <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <Card className="p-6 bg-card/50 border-gold/30">
            <p className="font-serif font-semibold text-white mb-4 flex items-center gap-2"><Gift className="w-4 h-4 text-gold" /> Partner benefits</p>
            <ul className="space-y-3">
              {PARTNER_BENEFITS.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-emerald-100/80">
                  <span className="text-gold mt-0.5">✦</span> {b}
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-4 mt-5 pt-5 border-t border-border/50 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5 text-gold" /> Referral links</span>
              <span className="flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-gold" /> Live earnings</span>
            </div>
          </Card>
        </div>
      </section>

      {/* Muallems */}
      {muallems.length > 0 && (
        <section className="px-4 sm:px-8 py-16 max-w-6xl mx-auto" data-testid="showcase-muallems">
          <p className="eyebrow text-gold text-center mb-2">Group Leaders</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">Meet Your Muallem</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {muallems.map((m) => (
              <Card key={m.id} data-testid={`showcase-muallem-${m.id}`} className="p-5 bg-card/40 border-border/60 card-hover flex items-start gap-4">
                {m.has_photo ? (
                  <img src={`${API}/public/muallems/${m.id}/photo`} alt={m.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-gold/50 flex-shrink-0" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-2xl font-serif text-gold flex-shrink-0">{m.name[0]}</div>
                )}
                <div className="min-w-0">
                  <p className="font-serif font-semibold text-white">{m.name}</p>
                  {m.experience && <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1.5"><Award className="w-3.5 h-3.5 text-gold mt-0.5 flex-shrink-0" />{m.experience}</p>}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="px-4 sm:px-8 pb-16 max-w-6xl mx-auto" data-testid="showcase-testimonials">
          <p className="eyebrow text-gold text-center mb-2">Reviews</p>
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">What Our Pilgrims Say</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {testimonials.map((t) => (
              <Card key={t.id} data-testid={`showcase-review-${t.id}`} className="p-5 bg-card/40 border-border/60 card-hover">
                <p className="text-gold">{"★".repeat(t.rating || 5)}</p>
                <p className="text-sm text-emerald-100/80 mt-2 leading-relaxed">"{t.text}"</p>
                <p className="text-xs text-white font-serif mt-3">{t.name}{t.location ? <span className="text-muted-foreground"> · {t.location}</span> : ""}</p>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      <section id="gallery" className="px-4 sm:px-8 pb-16 max-w-6xl mx-auto scroll-mt-20" data-testid="showcase-gallery">
        <p className="eyebrow text-gold text-center mb-2">Gallery</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-white text-center mb-10">Moments from our journeys</h2>
        {loading && <p className="text-sm text-muted-foreground text-center py-12">Loading gallery…</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-12">Photos & videos coming soon. Check back after our next Umrah group!</p>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <Card key={i.id} data-testid={`showcase-media-${i.id}`} className="overflow-hidden bg-card/40 border-border/60 card-hover">
              <div className="aspect-video bg-black/40 flex items-center justify-center overflow-hidden">
                {i.kind === "video" ? (
                  <video src={`${API}/public/media/${i.id}/file`} controls className="w-full h-full object-cover" />
                ) : (
                  <img src={`${API}/public/media/${i.id}/file`} alt={i.caption || i.original_filename} className="w-full h-full object-cover" />
                )}
              </div>
              <p className="p-3 text-xs text-muted-foreground truncate flex items-center gap-1.5">
                {i.kind === "video" ? <Play className="w-3 h-3 text-gold flex-shrink-0" /> : null}
                {i.caption || i.original_filename}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* Enquiry */}
      <section className="px-4 sm:px-8 pb-16 max-w-6xl mx-auto">
        <Card className="p-6 bg-card/40 border-gold/30 max-w-xl mx-auto" data-testid="enquiry-form-card">
          <p className="eyebrow text-gold text-center mb-1">Plan Your Umrah</p>
          <h2 className="font-serif text-lg font-semibold text-center mb-4">Send Us an Enquiry</h2>
          <form onSubmit={submitEnquiry} className="space-y-3" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Your name</Label>
                <Input data-testid="enquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Abdul Rahman" className="mt-1 bg-card/60" required />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Mobile number</Label>
                <Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+9198XXXXXXXX" className="mt-1 bg-card/60" required />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Message (package, dates, group size…)</Label>
              <Textarea data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="I am interested in the 15-day Umrah package for my family of 4…" className="mt-1 bg-card/60 min-h-[90px]" />
            </div>
            <Button type="submit" data-testid="enquiry-submit-btn" disabled={sending} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Enquiry
            </Button>
            <p className="text-[10px] text-muted-foreground text-center">Your enquiry goes straight to our customer care inbox. We reply on WhatsApp.</p>
          </form>
        </Card>
      </section>

      <footer className="border-t border-border/60 py-8 text-center">
        <img src={LOGO_URL} alt="Bengal Umrah House" className="w-10 h-10 rounded-full ring-1 ring-gold/50 mx-auto mb-3" />
        <p className="font-serif text-sm text-white">Bengal Umrah House</p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">© Bengal Umrah House Travels — All journeys planned with care.</p>
      </footer>
      </div>
    </div>
  );
}
